/**
 * WHERE A SIGNED-OUT LINK GOES AFTER SIGN-IN.
 *
 * The ticket-reminder SMS (api 0441) texts a hand a link to the job:
 * `/field/jobs/<id>`. Opened on a phone with no session, the signed-out tree's
 * catch-all sent it to a bare `/login`, and after signing in LoginScreen sent
 * everyone to their home screen. The job id was dropped at the first redirect,
 * so the link could not land on the job however the reader signed in.
 *
 * The target rides on the login URL as `?next=`, not in router state, so it
 * survives the reader reloading the login page or the OS reopening the tab.
 *
 * `safeNextPath` is the gate on it, because `next` is read off a URL anyone can
 * write: it accepts an in-app path only. `//evil.example` and `/\evil.example`
 * are protocol-relative to a browser and would leave the app; an absolute URL
 * would too. A refused value is ignored and the reader gets their home screen.
 */

const NEXT_PARAM = 'next';

/** An in-app path to return to, or null when `raw` is absent or unsafe. */
export function safeNextPath(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//') || raw.startsWith('/\\')) return null;
  // A browser strips tab, CR and LF out of a URL before parsing it, so
  // `/<TAB>/evil.example` is `//evil.example` by the time it is navigated to.
  // Any control character, or a backslash anywhere, is refused outright.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\\]/.test(raw)) return null;
  // Back to the login page is a loop, not a destination.
  if (raw === '/login' || raw.startsWith('/login?') || raw.startsWith('/login/')) return null;
  return raw;
}

/** The login URL for a signed-out visit to `location`, carrying it as `next`. */
export function loginPathFor(location: { pathname: string; search?: string; hash?: string }): string {
  const target = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
  if (location.pathname === '/' || !safeNextPath(target)) return '/login';
  return `/login?${NEXT_PARAM}=${encodeURIComponent(target)}`;
}

/** The `next` target carried on a login URL's query string, if it is safe. */
export function nextFromSearch(search: string): string | null {
  return safeNextPath(new URLSearchParams(search).get(NEXT_PARAM));
}
